import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { styled } from "styled-components";

const NewMpin = ({ handleReturnData, title }) => {
  const [loading, setloading] = useState(false);
  const [loadingIcon, setloadingIcon] = useState("");
  const [actionType, setActionType] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [otpLimitText, setOtpLimitText] = useState("");

  useEffect(() => {
    // Check localStorage on component mount to see if button should be disabled
    const clickData = JSON.parse(localStorage.getItem("mPinOtpLimit")) || {};
    const { clicks, timestamp } = clickData;
    const now = new Date().getTime();

    if (clicks >= 4 && timestamp && now - timestamp < 120000) {
      setIsButtonDisabled(true);
      const remainingTime = 120000 - (now - timestamp);
      setTimeout(() => resetClicks(), remainingTime);
    } else {
      resetClicks();
    }
  }, []);

  const resetClicks = () => {
    setIsButtonDisabled(false);
    setOtpLimitText("");
    localStorage.removeItem("mPinOtpLimit");
  };

  const handleButtonClick = () => {
    const clickData = JSON.parse(localStorage.getItem("mPinOtpLimit")) || {
      clicks: 0,
      timestamp: 0,
    };
    let { clicks } = clickData;
    clicks += 1;

    if (clicks >= 4) {
      setIsButtonDisabled(true);
      localStorage.setItem(
        "mPinOtpLimit",
        JSON.stringify({ clicks, timestamp: new Date().getTime() })
      );
      setOtpLimitText("Too many wrong attemps! Please try after 2mins.");
      setTimeout(() => resetClicks(), 120000); // 2-minute timer
    } else {
      localStorage.setItem(
        "mPinOtpLimit",
        JSON.stringify({ clicks, timestamp: clickData.timestamp })
      );
    }
  };

  const otpRefs = useRef([]);
  const addRef = (el) => {
    if (el && !otpRefs.current.includes(el)) {
      otpRefs.current.push(el);
    }
  };

  const handleInputChange = (e, index) => {
    formik.setFieldValue(`otp${index + 1}`, e.target.value);

    if (e.target.value.length === 1 && index < 4) {
      otpRefs.current[index + 1].focus();
    } else if (e.target.value === "" && e.keyCode === 8 && index > 0) {
      // keyCode 8 is for backspace
      otpRefs.current[index - 1].focus();
    }
  };

  const handleFocus = (e) => {
    // Set the cursor to the end
    const valueLength = e.target.value.length;
    e.target.setSelectionRange(valueLength, valueLength);
  };

  const handleKeyNavigation = (e, index) => {
    if (e.keyCode === 37 && index > 0) {
      // left arrow key
      otpRefs.current[index - 1].focus();
    } else if (e.keyCode === 39 && index < 3) {
      // right arrow key
      otpRefs.current[index + 1].focus();
    }
  };

  const validationSchema = Yup.object().shape({
    otp1: Yup.string().required("This field is required."),
    otp2: Yup.string().required("This field is required."),
    otp3: Yup.string().required("This field is required."),
    otp4: Yup.string().required("This field is required."),
    otp5: Yup.string().required("This field is required."),
  });

  const formik = useFormik({
    validationSchema: validationSchema,
    initialValues: {
      otp1: "",
      otp2: "",
      otp3: "",
      otp4: "",
      otp5: "",
    },
    onSubmit: async (data) => {
      const otp = data.otp1 + data.otp2 + data.otp3 + data.otp4 + data.otp5;
      data["otp"] = otp;
      delete data["otp1"];
      delete data["otp2"];
      delete data["otp3"];
      delete data["otp4"];
      delete data["otp5"];
      try {
        setloading(true);
        setloadingIcon("pi pi-spin pi-spinner");
        let api = "";
        if (actionType === "r") {
          api = "zbizgateway/accountMaintainence/regenerateLoginPin";
        } else if (actionType === "c") {
          api = "zbizgateway/accountMaintainence/changePin";
        }
        await handleReturnData(data, api);
        setloading(false);
        setloadingIcon("pi pi-save");
      } catch (error) {
        console.log(error);
      } finally {
        setloading(false);
        setloadingIcon("pi pi-save");
        formik.resetForm();
      }
    },
  });
  const isFormFieldValid = (name) =>
    !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    );
  };

  useEffect(() => {
    if (
      !formik.values.otp1 ||
      !formik.values.otp2 ||
      !formik.values.otp3 ||
      !formik.values.otp4 ||
      !formik.values.otp5
    ) {
      setDisableSubmit(true);
    } else setDisableSubmit(false);
  }, [
    formik.values.otp1,
    formik.values.otp2,
    formik.values.otp3,
    formik.values.otp4,
    formik.values.otp5,
  ]);

  return (
    <div className="card Card__Round">
      <div className="Form__Header">
        <h1> {title} </h1>
      </div>

      <form onSubmit={formik.handleSubmit} className="p-fluid p-mt-2">
        <div className="p-mt-4">
          <div>
            <p>
              Please enter the OTP code sent to your registered Mobile Number /
              Email Address. <br />
            </p>
            <div>
              {[1, 2, 3, 4, 5].map((_, index) => (
                <Input
                  key={index}
                  maxLength="1"
                  ref={addRef}
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => handleKeyNavigation(e, index)}
                  onFocus={handleFocus}
                  value={formik.values[`otp${index + 1}`]}
                  name={`otp${index + 1}`}
                  type="text"
                />
              ))}
              {getFormErrorMessage("otp5")}
            </div>
            <small
              className="p-mt-4"
              style={{ display: "block", color: "red" }}
            >
              {otpLimitText && otpLimitText}
            </small>
          </div>
        </div>

        <div className="Down__Btn" style={{ textAlign: "left" }}>
          <Button
            onClick={() => {
              setActionType("r");
              handleButtonClick();
            }}
            icon={`${isButtonDisabled ? "pi pi-ban" : ""}`}
            disabled={isButtonDisabled || loading || disableSubmit}
            label={"Regenerate Login Pin"}
            className="Btn__Dark"
            type="submit"
          />
          {/* <Button
                        onClick={() => {
                            setActionType("c");
                            formik.handleSubmit();
                        }}
                        disabled={loading}
                        icon={loadingIcon || ""}
                        iconPos="right"
                        label={"Change Financial Pin"}
                        className="Btn__Dark"
                    /> */}
        </div>
      </form>
    </div>
  );
};

const Input = styled.input`
  width: 40px;
  margin: 0 5px;
  text-align: center;
`;

export default NewMpin;
